/**
 * Audio utilities for Gemini Live Native Audio
 * 
 * These utilities handle PCM audio encoding/decoding for
 * real-time bidirectional audio streaming with Gemini.
 */

export interface AudioBlob {
  data: string;
  mimeType: string;
}

/**
 * Convert base64 string to Uint8Array
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert ArrayBuffer to base64 string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Decode PCM audio data to AudioBuffer
 * 
 * @param data - Raw PCM data as Uint8Array
 * @param ctx - AudioContext for creating buffer
 * @param sampleRate - Sample rate (e.g., 24000 for output)
 * @param numChannels - Number of audio channels
 */
export async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

/**
 * Create PCM audio blob from Float32Array (legacy base64 version)
 *
 * @param data - Float32Array audio samples
 * @returns AudioBlob with base64 data and mime type
 */
export function createPcmBlob(data: Float32Array): AudioBlob {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    int16[i] = Math.max(-32768, Math.min(32767, data[i] * 32768));
  }
  return {
    data: arrayBufferToBase64(int16.buffer),
    mimeType: 'audio/pcm;rate=16000',
  };
}

/**
 * Create raw PCM bytes from Float32Array (optimized binary version)
 *
 * @param data - Float32Array audio samples
 * @returns ArrayBuffer containing raw PCM int16 data
 */
export function createPcmBytes(data: Float32Array): ArrayBuffer {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    int16[i] = Math.max(-32768, Math.min(32767, data[i] * 32768));
  }
  return int16.buffer;
}

/**
 * Calculate RMS volume from audio samples
 */
export function calculateVolume(samples: Float32Array): number {
  let sum = 0;
  const step = 8;
  for (let i = 0; i < samples.length; i += step) {
    sum += samples[i] * samples[i];
  }
  return Math.sqrt(sum / (samples.length / step)) * 5;
}

